import { useEffect, useMemo, useRef, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { http } from "@/lib/api";
import { toast } from "sonner";
import { useAuth } from "@/contexts/AuthContext";
import LangInput from "@/components/LangInput";
import ListenTranslateBar from "@/components/ListenTranslateBar";
import ShareButton from "@/components/ShareButton";
import PhotoIdentify from "@/components/PhotoIdentify";
import { fpPath } from "@/lib/site";
import {
  ArrowLeft, Sparkle, PaperPlaneTilt, Warning, House, MapPin, Notebook,
  ChatCircleDots, Certificate, Question, ArrowRight, Camera,
} from "@phosphor-icons/react";

/**
 * FarmersAsk — /farmers/ask
 *
 * Chat UI for the grounded Farmers AI Assistant. Every answer shows the
 * confidence level and the sources it used (farm, soil #1, nearby report
 * #3, …) so the farmer knows what the answer is standing on.
 */
const STARTER_QUESTIONS = [
  "What can I plant this week where I am?",
  "Are there pests or diseases building up nearby?",
  "How does this year's rainfall compare with last year on my farm?",
  "My tomato leaves are yellowing — what's likely wrong?",
  "When should I harvest my bananas?",
];

export default function FarmersAsk() {
  const { user, requireSignIn } = useAuth();
  const [params] = useSearchParams();
  const [q, setQ] = useState("");
  const [turns, setTurns] = useState([]);
  const [busy, setBusy] = useState(false);
  const [sessionId, setSessionId] = useState(() => `ask_${Date.now().toString(36)}`);
  const [farms, setFarms] = useState([]);
  const [farmId, setFarmId] = useState(params.get("farm_id") || "");
  const [loc, setLoc] = useState(() => {
    try { const raw = localStorage.getItem("np:pulse-location"); return raw ? JSON.parse(raw) : null; }
    catch { return null; }
  });
  const chatRef = useRef(null);

  useEffect(() => {
    if (!user) return;
    http.get("/farmers/farms/mine").then(({ data }) => setFarms(data.farms || [])).catch(() => {});
  }, [user]);

  useEffect(() => {
    if (chatRef.current) chatRef.current.scrollTop = chatRef.current.scrollHeight;
  }, [turns]);

  const useMyLocation = () => {
    if (!navigator.geolocation) return toast.error("Geolocation is not supported here.");
    navigator.geolocation.getCurrentPosition((p) => {
      const l = { lat: p.coords.latitude, lon: p.coords.longitude, label: "My location" };
      setLoc(l);
      try { localStorage.setItem("np:pulse-location", JSON.stringify(l)); } catch { /* noop */ }
    }, () => toast.error("Could not read your location."));
  };

  const ask = async (question) => {
    const clean = (question || "").trim();
    if (clean.length < 3) return;
    setBusy(true);
    // Optimistic turn — mark it "pending" so the UI shows a spinner right away.
    const pending = { pending: true, question: clean, id: `pending_${Date.now()}` };
    setTurns((t) => [...t, pending]);
    try {
      const { data } = await http.post("/farmers/ask", {
        question: clean,
        latitude: loc?.lat, longitude: loc?.lon,
        farm_id: farmId || undefined,
        session_id: sessionId,
      });
      setTurns((t) => t.map((row) => row.id === pending.id ? { ...data, question: clean, id: pending.id } : row));
    } catch (e) {
      setTurns((t) => t.filter((row) => row.id !== pending.id));
      toast.error(e?.response?.data?.detail || "AI could not answer — try again.");
    } finally {
      setBusy(false);
      setQ("");
    }
  };

  const summaryText = useMemo(() => {
    const bits = [];
    if (loc?.lat) bits.push(`location ${loc.lat.toFixed(2)}, ${loc.lon.toFixed(2)}`);
    if (farmId) bits.push(`farm selected`);
    return bits.join(" · ");
  }, [loc, farmId]);

  const heroText = "Farmers Pulse Assistant — ask a plain-language question. Answers ground on your farm, soil records, water history and nearby community observations. When the assistant is uncertain, it says so.";

  return (
    <main className="bg-[#F5EBDC] min-h-screen pt-32 md:pt-36 pb-32 px-5 md:px-10" data-testid="farmers-ask-page">
      <div className="max-w-[1100px] mx-auto">
        <Link to={fpPath("/")} className="inline-flex items-center gap-1.5 font-mono-tech text-[10px] tracking-[0.32em] uppercase text-[#3A3830] hover:text-[#151312]">
          <ArrowLeft size={11} weight="bold" /> Farmers Pulse
        </Link>
        <div className="mt-3 font-mono-tech text-[10px] tracking-[0.32em] uppercase text-[color:var(--np-brand,#B85F2A)] flex items-center gap-2">
          <Sparkle size={12} weight="fill" /> / Ask Farmers Pulse
        </div>
        <h1 className="mt-2 font-serif-display text-4xl md:text-5xl leading-[0.98] tracking-tight">
          Ask a plain-language question.
        </h1>
        <p className="mt-3 max-w-2xl text-sm md:text-base text-[#3A3830]">
          Answers ground on your farm, soil records, water history and nearby community observations. When the assistant is uncertain, it says so.
        </p>

        <div className="mt-4 flex flex-wrap items-center gap-2">
          <ListenTranslateBar text={heroText} testidPrefix="farmers-ask-tools" />
          <ShareButton title="Farmers Pulse Assistant" text={heroText} variant="chip" testid="farmers-ask-share" />
          <PhotoIdentify
            variant="chip"
            locationContext={loc?.lat ? `${loc.lat.toFixed(3)}, ${loc.lon.toFixed(3)}` : null}
            testid="ask-photo-identify"
          />
        </div>

        {params.get("mode") === "photo" && (
          <section className="mt-6 rounded-2xl border border-[#2D8659]/30 bg-white p-4 md:p-5" data-testid="ask-photo-inline">
            <div className="font-mono-tech text-[10px] tracking-[0.32em] uppercase text-[#2D8659] flex items-center gap-1.5 mb-3">
              <Camera size={12} weight="fill" /> Photo identification
            </div>
            <PhotoIdentify
              variant="inline"
              locationContext={loc?.lat ? `${loc.lat.toFixed(3)}, ${loc.lon.toFixed(3)}` : null}
              testid="ask-photo-inline-body"
            />
          </section>
        )}

        {/* Grounding controls */}
        <section className="mt-6 rounded-2xl border border-black/10 bg-white p-4 flex flex-wrap items-center gap-3" data-testid="farmers-ask-grounding">
          <button
            onClick={useMyLocation}
            data-testid="farmers-ask-use-location"
            className="inline-flex items-center gap-1.5 rounded-full border border-black/20 bg-white px-3 py-1.5 font-mono-tech text-[10px] tracking-[0.28em] uppercase hover:bg-black/5"
          >
            <MapPin size={11} weight="fill" /> {loc?.lat ? "Update location" : "Use my location"}
          </button>
          {user && farms.length > 0 && (
            <label className="inline-flex items-center gap-2 text-xs">
              <span className="font-mono-tech text-[10px] tracking-[0.28em] uppercase text-[#3A3830]">Farm</span>
              <select
                value={farmId}
                onChange={(e) => setFarmId(e.target.value)}
                data-testid="farmers-ask-farm"
                className="bg-white border border-black/15 rounded-lg px-2 py-1 text-sm"
              >
                <option value="">— none —</option>
                {farms.map((f) => <option key={f.id} value={f.id}>{f.name}</option>)}
              </select>
            </label>
          )}
          {summaryText && (
            <div className="ml-auto text-[10px] font-mono-tech tracking-[0.22em] uppercase text-[#3A3830]" data-testid="farmers-ask-context">
              Grounded on: {summaryText}
            </div>
          )}
        </section>

        {/* Starter chips */}
        {turns.length === 0 && (
          <section className="mt-6" data-testid="farmers-ask-starters">
            <div className="font-mono-tech text-[10px] tracking-[0.28em] uppercase text-[#3A3830]">Try asking</div>
            <div className="mt-2 flex flex-wrap gap-2">
              {STARTER_QUESTIONS.map((s) => (
                <button
                  key={s}
                  onClick={() => ask(s)}
                  data-testid="farmers-ask-starter"
                  disabled={busy}
                  className="rounded-full border border-black/15 bg-white px-3 py-1.5 text-xs hover:border-[color:var(--np-brand,#B85F2A)] disabled:opacity-50 text-left"
                >
                  {s}
                </button>
              ))}
            </div>
          </section>
        )}

        {/* Chat */}
        <section
          ref={chatRef}
          className="mt-6 rounded-2xl border border-black/10 bg-white p-4 md:p-6 max-h-[520px] overflow-y-auto space-y-5"
          data-testid="farmers-ask-chat"
        >
          {turns.length === 0 && (
            <div className="text-center text-sm text-[#3A3830] py-8" data-testid="farmers-ask-empty">
              Your conversation will appear here. Ask a question below.
            </div>
          )}
          {turns.map((t) => <Turn key={t.id} turn={t} />)}
        </section>

        {/* Composer */}
        <section className="mt-4 rounded-2xl border border-black/10 bg-white p-3 flex items-end gap-2" data-testid="farmers-ask-composer">
          <div className="flex-1">
            <LangInput
              as="textarea"
              rows={2}
              value={q}
              onChange={(e) => setQ(e.target.value)}
              testid="farmers-ask-input"
              placeholder="Ask about crops, pests, soil, water, calendar, rain — anything about your farm."
            />
          </div>
          <button
            onClick={() => ask(q)}
            disabled={busy || q.trim().length < 3}
            data-testid="farmers-ask-submit"
            className="inline-flex items-center gap-1.5 rounded-full bg-[#151312] text-[#F5EBDC] px-4 py-2 font-mono-tech text-[10px] tracking-[0.28em] uppercase disabled:opacity-50"
          >
            <PaperPlaneTilt size={11} weight="fill" /> {busy ? "Asking…" : "Ask"}
          </button>
        </section>

        <div className="mt-4 rounded-lg border border-amber-200 bg-amber-50 text-amber-900 p-3 text-xs flex items-start gap-2" data-testid="farmers-ask-disclaimer">
          <Warning size={12} weight="fill" className="mt-0.5" />
          <span>AI interpretation of publicly available data — not a substitute for extension advice. High-stakes decisions (pesticides, spraying, disease diagnosis) should be verified with a qualified agronomist.</span>
        </div>
      </div>
    </main>
  );
}

function Turn({ turn }) {
  return (
    <div className="grid gap-3" data-testid={turn.pending ? "ask-turn-pending" : "ask-turn"}>
      {/* Question bubble */}
      <div className="flex items-start gap-2 justify-end">
        <div className="rounded-2xl rounded-br-sm bg-[#241209] text-[#F5EBDC] px-4 py-2 max-w-[80%] text-sm">
          {turn.question}
        </div>
        <ChatCircleDots size={16} weight="fill" className="text-[#241209] mt-1" />
      </div>
      {/* Answer bubble */}
      <div className="flex items-start gap-2">
        <Sparkle size={16} weight="fill" className="text-[color:var(--np-brand,#B85F2A)] mt-1" />
        <div className="rounded-2xl rounded-bl-sm bg-[#FBF2E1] px-4 py-3 max-w-[85%] text-sm text-[#151312]">
          {turn.pending ? (
            <div className="flex items-center gap-1.5 text-[#3A3830]" data-testid="ask-answer-pending">
              <span className="w-1.5 h-1.5 bg-[color:var(--np-brand,#B85F2A)] rounded-full animate-bounce" style={{ animationDelay: "0s" }} />
              <span className="w-1.5 h-1.5 bg-[color:var(--np-brand,#B85F2A)] rounded-full animate-bounce" style={{ animationDelay: "0.15s" }} />
              <span className="w-1.5 h-1.5 bg-[color:var(--np-brand,#B85F2A)] rounded-full animate-bounce" style={{ animationDelay: "0.3s" }} />
              <span className="ml-2 font-mono-tech text-[10px] tracking-[0.22em] uppercase">Grounding on your data…</span>
            </div>
          ) : (
            <>
              <div className="whitespace-pre-wrap leading-relaxed">{turn.answer || "No answer."}</div>
              {(turn.confidence || (turn.citations || []).length > 0) && (
                <div className="mt-3 flex flex-wrap gap-1.5 items-center">
                  {turn.confidence && (
                    <span
                      data-testid="ask-confidence"
                      className={`inline-flex items-center gap-1 rounded-full px-2 py-0.5 font-mono-tech text-[9px] tracking-[0.22em] uppercase border ${
                        turn.confidence === "high"
                          ? "bg-emerald-50 border-emerald-200 text-emerald-800"
                          : turn.confidence === "medium"
                          ? "bg-amber-50 border-amber-200 text-amber-800"
                          : "bg-stone-50 border-stone-200 text-stone-700"
                      }`}
                    >
                      <Certificate size={9} weight="fill" /> {turn.confidence} confidence
                    </span>
                  )}
                  {(turn.citations || []).map((c) => (
                    <span key={c} data-testid="ask-citation" className="inline-flex items-center rounded-full bg-white border border-black/15 px-2 py-0.5 font-mono-tech text-[9px] tracking-[0.22em] uppercase">
                      {c}
                    </span>
                  ))}
                </div>
              )}
              {(turn.follow_ups || []).length > 0 && (
                <div className="mt-3">
                  <div className="font-mono-tech text-[9px] tracking-[0.22em] uppercase text-[#3A3830]">Follow up</div>
                  <div className="mt-1 flex flex-wrap gap-1.5">
                    {turn.follow_ups.map((f) => (
                      <a
                        key={f}
                        href={`?fu=${encodeURIComponent(f)}`}
                        onClick={(e) => { e.preventDefault(); /* let user click Ask; keep simple */ }}
                        className="rounded-full bg-white border border-black/15 px-2 py-0.5 text-[11px] hover:border-[color:var(--np-brand,#B85F2A)]"
                        data-testid="ask-followup"
                      >
                        <Question size={10} weight="fill" className="inline mr-1" />{f}
                      </a>
                    ))}
                  </div>
                </div>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
}
